import { Link } from '@inertiajs/react'
import { Menu, X, ArrowRight, LayoutDashboard, LogOut, ChevronDown, ExternalLink } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import type { MarketingLayoutProps } from '@/types/marketing'

function isInternal(href: string) {
  return href.startsWith('/')
}

function NavLink({ href, label, active }: { href: string; label: string; active: boolean }) {
  const className = `marketing-nav-link ${active ? 'marketing-nav-link-active' : ''}`
  return isInternal(href) ? (
    <Link href={href} className={className} prefetch>
      {label}
    </Link>
  ) : (
    <a href={href} className={className}>
      {label}
    </a>
  )
}

export default function MarketingNav({ layout }: { layout: MarketingLayoutProps }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const navRef = useRef<HTMLElement>(null)
  const currentPath = layout.current_path

  useEffect(() => {
    if (!mobileOpen) return
    function handleClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMobileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [mobileOpen])
  // Canonical link list comes through layout.nav.items from
  // MarketingNavHelper#marketing_nav_items, so adding or renaming a nav
  // item only happens in one place.
  const navItems = layout.nav.items

  // Resolve display label: workspace name for owners, first name for members
  const auth = layout.auth
  const userLabel = auth.workspace_name || auth.first_name || auth.email

  return (
    <header className="marketing-nav-wrap">
      <nav ref={navRef} className="container mx-auto px-6 py-4">
        <div className="marketing-nav">
          <Link href={layout.nav.home_path} className="flex items-center gap-3" prefetch>
            {/* h-9 (36px) reads more confident on desktop than the original
                h-7 without forcing the nav taller — buttons (~48px) and
                hamburger (~40px) still dictate the overall row height.
                Mobile keeps the original h-7 so the logo doesn't crowd the
                hamburger toggle on narrow viewports. */}
            <img src={layout.logo_url} alt="wavbee" className="h-7 w-auto lg:h-9" />
          </Link>

          <div className="hidden items-center gap-7 lg:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                href={item.href}
                label={item.label}
                active={currentPath === item.href}
              />
            ))}
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            {auth.signed_in ? (
              <UserMenu
                label={userLabel ?? ''}
                initial={userLabel?.charAt(0)?.toUpperCase() ?? '?'}
                email={auth.email ?? ''}
                dashboardPath={auth.dashboard_path ?? '/dashboard'}
                logoutPath={auth.logout_path ?? '/logout'}
                distributionDashboardUrl={auth.has_workspace ? layout.nav.distribution_dashboard_url ?? undefined : undefined}
              />
            ) : (
              <>
                <a href={layout.nav.login_path} className="marketing-button-secondary">
                  Login
                </a>
                <a href={layout.nav.apply_path} className="marketing-button-primary">
                  Apply Now
                  <ArrowRight className="h-4 w-4" />
                </a>
              </>
            )}
          </div>

          <button
            type="button"
            className="marketing-mobile-toggle lg:hidden"
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
            aria-controls="marketing-mobile-menu"
            onClick={() => setMobileOpen((value) => !value)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div id="marketing-mobile-menu" className="marketing-mobile-panel lg:hidden" onClick={() => setMobileOpen(false)}>
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  active={currentPath === item.href}
                />
              ))}
              {auth.signed_in ? (
                <>
                  <div className="text-sm" style={{ color: '#94a3b8' }}>{userLabel}</div>
                  {auth.has_workspace && layout.nav.distribution_dashboard_url && (
                    <a
                      href={layout.nav.distribution_dashboard_url}
                      target="_blank"
                      rel="noopener"
                      className="marketing-button-secondary"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Distribution Dashboard
                    </a>
                  )}
                  <a href={auth.dashboard_path ?? '/dashboard'} className="marketing-button-secondary">
                    Account Hub
                  </a>
                  <form action={auth.logout_path ?? '/logout'} method="post" style={{ display: 'inline' }}>
                    <input type="hidden" name="_method" value="delete" />
                    <input type="hidden" name="authenticity_token" value={getCSRFToken()} />
                    <button type="submit" className="marketing-button-ghost" style={{ width: '100%', textAlign: 'left' }}>
                      <LogOut className="h-4 w-4" />
                      Log out
                    </button>
                  </form>
                </>
              ) : (
                // 2-column grid so Login + Apply Now sit side-by-side instead
                // of stacking — keeps the mobile menu compact and the
                // primary auth pair always visible in one row. The CSS
                // override on .marketing-mobile-panel .marketing-button-*
                // tightens padding so the row doesn't tower over the nav
                // links above it.
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <a href={layout.nav.login_path} className="marketing-button-secondary">
                    Login
                  </a>
                  <a href={layout.nav.apply_path} className="marketing-button-primary">
                    Apply Now
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}

/* ── User Menu Dropdown ──────────────────────────────────────────────────── */

function UserMenu({
  label,
  initial,
  email,
  dashboardPath,
  logoutPath,
  distributionDashboardUrl,
}: {
  label: string
  initial: string
  email: string
  dashboardPath: string
  logoutPath: string
  distributionDashboardUrl?: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="marketing-user-chip"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="marketing-user-chip-avatar">{initial}</span>
        <span style={{ maxWidth: '10rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <ChevronDown className="h-3 w-3" style={{ opacity: 0.5, flexShrink: 0 }} />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 0.5rem)',
            minWidth: '13rem',
            background: '#111',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '0.5rem',
            padding: '0.25rem',
            zIndex: 50,
          }}
        >
          <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#e2e8f0' }}>{label}</p>
            <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.125rem' }}>{email}</p>
          </div>
          {distributionDashboardUrl && (
            <a
              href={distributionDashboardUrl}
              target="_blank"
              rel="noopener"
              role="menuitem"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                fontSize: '0.8rem',
                color: '#cbd5e1',
                textDecoration: 'none',
                borderRadius: '0.375rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Distribution Dashboard
            </a>
          )}
          <a
            href={dashboardPath}
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              fontSize: '0.8rem',
              color: '#cbd5e1',
              textDecoration: 'none',
              borderRadius: '0.375rem',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            <LayoutDashboard className="h-3.5 w-3.5" />
            Account Hub
          </a>
          <form action={logoutPath} method="post">
            <input type="hidden" name="_method" value="delete" />
            <input type="hidden" name="authenticity_token" value={getCSRFToken()} />
            <button
              type="submit"
              role="menuitem"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                fontSize: '0.8rem',
                color: '#fca5a5',
                background: 'transparent',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                borderRadius: '0.375rem',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              <LogOut className="h-3.5 w-3.5" />
              Log out
            </button>
          </form>
        </div>
      )}
    </div>
  )
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function getCSRFToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}
